import type { ReactNode } from "react";
import { cn } from "@/utils/cn";

export interface ChoiceOption {
  value: string;
  label: ReactNode;
}

interface ChoiceGroupBaseProps {
  /** Fieldset id. Continue scrolls to and focuses it when the question is unanswered. */
  id: string;
  /** The question. Rendered as the fieldset legend, so screen readers announce it with each choice. */
  legend: ReactNode;
  /** Short helper after the legend, e.g. "Select all that apply". */
  hint?: ReactNode;
  options: ChoiceOption[];
  /** Shown under the choices and linked with aria-describedby (e.g. "Answer this to continue"). */
  error?: string | null;
  /** "numbers" lays 0–10 chips out evenly; "segmented" fills the row (Left / Right / Both). */
  layout?: "pills" | "numbers" | "segmented";
  required?: boolean;
  className?: string;
}

interface SingleChoiceProps extends ChoiceGroupBaseProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
  /** Tapping the selected choice again clears the answer (native radios can't be unselected). */
  clearable?: boolean;
}

interface MultiChoiceProps extends ChoiceGroupBaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export type ChoiceGroupProps = SingleChoiceProps | MultiChoiceProps;

/**
 * One question answered with pill-shaped choices. Built on native radio / checkbox inputs so the
 * browser supplies grouping, checked state and arrow-key movement; the inputs are visually hidden and
 * the label is the 44px pill. Nothing is selected until the patient picks something.
 */
export function ChoiceGroup(props: ChoiceGroupProps) {
  const { id, legend, hint, options, error, layout = "pills", required, className } = props;
  const errorId = `${id}-error`;
  const inputType = props.multiple ? "checkbox" : "radio";

  const isChecked = (value: string) =>
    props.multiple ? props.value.includes(value) : props.value === value;

  const handleChange = (value: string) => {
    if (props.multiple) {
      const next = props.value.includes(value)
        ? props.value.filter((v) => v !== value)
        : [...props.value, value];
      props.onChange(next);
    } else {
      props.onChange(value);
    }
  };

  // A click on the radio that is already checked fires no change event; treat it as "clear".
  // `props.value` is the value from this render, so a click that just selected a new choice
  // (including arrow-key selection, which also fires click) never matches here.
  const handleRadioClick = (value: string) => {
    if (!props.multiple && props.clearable && props.value === value) props.onChange(null);
  };

  return (
    <fieldset
      id={id}
      tabIndex={-1}
      aria-describedby={error ? errorId : undefined}
      aria-required={required || undefined}
      className={cn("choice-group", error && "choice-group--error", className)}
    >
      <legend className="choice-group__legend">
        {legend}
        {hint && <span className="choice-group__hint"> {hint}</span>}
      </legend>
      <div className={cn("choice-group__options", `choice-group__options--${layout}`)}>
        {options.map((option) => {
          const checked = isChecked(option.value);
          return (
            <label key={option.value} className={cn("choice-pill", checked && "choice-pill--checked")}>
              <input
                type={inputType}
                name={id}
                value={option.value}
                checked={checked}
                onChange={() => handleChange(option.value)}
                onClick={() => handleRadioClick(option.value)}
                className="choice-pill__input"
              />
              {checked && <i className="fas fa-check choice-pill__check" aria-hidden />}
              <span>{option.label}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="choice-group__error" role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
